import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';

import template from './imageCropper.html';
import '../imageCropper.scss';

/**
 * @ngdoc controller
 * @name odsImageCropperController
 * @description
 * This a wrapper for the Cropper.js library. Creates a cropper js instance and adds functionality to
 * connect cropper instance to angular component bindings.
 */
class ImageCropperController {
  /**
   * @param {Service} $element To get the element in which to mount the cropper.
   *
   * @ignore
   */
  constructor($element) {
    'ngInject';

    /**
     * HTML image element where the cropper will be mounted.
     * @type {HTMLElement}
     * @access protected
     */
    [this._imageCanvas] = Array.from($element.find('img'));

     /**
     * Flag to evaluate if the image has a rotate angle.
     * @type {Boolean}
     */
    this.isOrientationRotated = false;
    /**
     * Map of handlers for changed bindings.
     * @type {Array<Object>}
     * @property {String}   binding          This the binding to check for changes.
     * @property {Function} fn               Function to execute if the binding changed.
     * @property {Boolean}  checkValueChange If `true` it will check if the binding's value has changed. If `false` it
     *                                       will only check for `true` binding values.
     * @access protected
     */
    this._changeHandlers = [
      {
        binding: 'image',
        fn: this._destroy.bind(this),
        checkValueChange: true,
      },
      {
        binding: 'zoomFactor',
        fn: this._zoom.bind(this),
        checkValueChange: true,
      },
      {
        binding: 'rotateFactor',
        fn: this._rotate.bind(this),
        checkValueChange: true,
      },
      {
        binding: 'reset',
        fn: this._resetCropper.bind(this),
        checkValueChange: false,
      },
      {
        binding: 'crop',
        fn: this._cropImage.bind(this),
        checkValueChange: false,
      },
      {
        binding: 'fit',
        fn: this._fit.bind(this),
        checkValueChange: false,
      },
      {
        binding: 'fill',
        fn: this._fill.bind(this),
        checkValueChange: false,
      },
    ];

    /**
     * @ignore
     */
    this._mountCropper = this._mountCropper.bind(this);
  }

  /**
   * On component initialization add a listener for the load event of the image to mount the cropper as soon as
   * the image is loaded.
   */
  $onInit() {
    this._imageCanvas.addEventListener('load', this._mountCropper);
    if (angular.isUndefined(this.cropBoxSizeIncreaseFactor)) {
      this.cropBoxSizeIncreaseFactor = 1.5;
    }
    if (angular.isUndefined(this.cropBoxSizeThreshold)) {
      this.cropBoxSizeIncreaseFactor = 300;
    }
  }

  /**
   * When bindings changes, it will check if the changed binding is present in the change handlers map, and
   * perform the action from the handler.
   * @param {Object} changes An object with all the bindings that were changed
   */
  $onChanges(changes) {
    if (this._isCropperReady()) {
      this._changeHandlers.forEach(({ binding, fn, checkValueChange }) => {
        const change = changes[binding];
        if (change && (checkValueChange ? change.previousValue !== change.currentValue : change.currentValue)) {
          fn(change.currentValue);
        }
      });
    }
  }

  /**
   * It destroys the current cropper instance.
   * @access protected
   */
  _destroy() {
    this._cropper.destroy();
  }

  /**
   * Checks if a cropper instance exists and its ready to use.
   * @return {Boolean}
   * @access protected
   */
  _isCropperReady() {
    return this._cropper && this._cropper.ready;
  }

  /**
   * It zooms the cropper component.
   * @param {Number} value
   * @access protected
   */
  _zoom(value) {
    this._cropper.zoomTo(value);
  }

  /**
   * It rotates the image from the cropper to the given degrees value. And makes a call
   * to the local `onRotate` callback.
   * @param {Number} value
   * @access protected
   */
  _rotate(value) {
    if (!value) {
      return;
    }

    this._cropper.rotate(value);
    const { width: canvasWidth, height: canvasHeight } = this._cropper.getCanvasData();
    const { width: cropBoxWidth, height: cropBoxHeight } = this._cropper.getCropBoxData();

    if (this.fixedCropBox) {
      this._centerCanvas();
      if (Math.round(canvasHeight) === Math.round(cropBoxHeight)) {
        this._fitDimension = 'height';
      } else if (Math.round(canvasWidth) === Math.round(cropBoxWidth)) {
        this._fitDimension = 'width';
      }
    } else {
      /**
       * Get the container data after rotate and fit the image into the container limits.
       * Set the cropper data to fit the image.
       */
      const { width: containerWidth, height: containerHeight } = this._cropper.getContainerData();
      const { naturalWidth, naturalHeight } = this._cropper.getImageData();

      // Checking with an XOR if the image is rotated and if it is portrait.
      if (this._isRotated() !== Math.round(naturalWidth) > Math.round(naturalHeight)) {
        this._changeCanvasDataAfterRotate(
          canvasWidth > containerWidth,
          { width: containerWidth },
        );
      } else {
        this._changeCanvasDataAfterRotate(
          canvasHeight > containerHeight,
          { height: containerHeight },
        );
      }

      this._centerCanvas();

      if (this.fill) {
        this._fill(false);
      } else {
        this._fit(false);
      }
    }

    this.onRotate();
  }

  /**
   * Given the provided flag change the aspect ratio and set the provided canvas data.
   * @param {Boolean} flag  The flag to check.
   * @param {Boolean} data  The data to set to the canvas.
   * @access protected
   */
  _changeCanvasDataAfterRotate(flag, data) {
    if (flag) {
      this._autoAspectRatio();
      this._cropper.setCanvasData(data);
    }
  }

  /**
   * It executes the local `onCrop` callback and pass the cropped image data as argument.
   * @access protected
   */
  _cropImage() {
    this.onCrop({
      croppedImageData: this._cropper.crop(),
      croppedImage: this._cropper.getCroppedCanvas().toDataURL(),
    });
  }

  /**
   * It resets the cropper to its initial value and makes a call to the local `onReset` callback.
   * @access protected
   */
  _resetCropper() {
    this._cropper.reset();
    this._zoom(this.zoomFactor);
    this._resizeCropBox();

    if (this.fill) {
      this._fill(false);
    } else {
      this._fit(false);
    }
    this.onReset();
  }

  /**
   * If the cropBox is fixed then it adjusts the size of the canvas to fit the crop box width, if the resulting
   * height of this is greater than the crop box height, then it adjust the size of the canvas to fit the crop
   * box height. Otherwise, it fits the crop box to the image size.
   * @param {Boolean} [triggerCallback=true] Whether to trigger callback or not, defaults to true. When this method
   *                                         execution is triggered from inside the component(when the cropper is
   *                                         mounted) there is no need to trigger a callback.
   * @access protected
   */
  _fit(triggerCallback = true) {
    const { width: canvasWidth, height: canvasHeight } = this._cropper.getCanvasData();

    if (this.fixedCropBox) {
      const { width: cropBoxWidth, height: cropBoxHeight } = this._cropper.getCropBoxData();

      this._cropper.setCanvasData({ width: cropBoxWidth, minWidth: cropBoxWidth });
      this._fitDimension = 'width';
      if (canvasHeight > cropBoxHeight) {
        this._fitDimension = 'height';
        this._cropper.setCanvasData({ width: 'auto', height: cropBoxHeight });
      } else if (canvasHeight === cropBoxHeight) {
        this._fitDimension = 'perfect';
      }

      this._centerCanvas();
    } else {
      this._autoAspectRatio();
      if (this.isOrientationRotated && this._isRotated()) {
        this._cropper.setCropBoxData({ width: canvasHeight, height: canvasWidth });
      } else {
        this._cropper.setCropBoxData({ width: canvasWidth, height: canvasHeight });
      }
    }

    if (triggerCallback) {
      this.onFit({ minZoomLevel: this._calculateMinZoom() });
    }
  }

  /**
   * If the cropBox is fixed it adjusts the size of the canvas to fit the container width, if the resulting
   * height of this is greater than the container height, then it adjust the size of the canvas to fit the
   * container height. Otherwise it just force the crop box to a square size.
   * @param {Boolean} [triggerCallback=true] Whether to trigger callback or not, defaults to true. When this method
   *                                         execution is triggered from inside the component(when the cropper is
   *                                         mounted) there is no need to trigger a callback.
   * @access protected
   */
  _fill(triggerCallback = true) {
    if (this.fixedCropBox) {
      const { height: canvasHeight } = this._cropper.getCanvasData();
      const { width: cropBoxWidth, height: cropBoxHeight } = this._cropper.getCropBoxData();

      this._cropper.setCanvasData({ width: cropBoxWidth });

      if (canvasHeight < cropBoxHeight) {
        this._cropper.setCanvasData({ width: 'auto', height: cropBoxHeight });
      }

      this._centerCanvas();
    } else {
      this._cropper.setAspectRatio(1);
    }

    if (triggerCallback) {
      this.onFill({ minZoomLevel: this._calculateMinZoom() });
    }
  }

  /**
   * It calculates the minimum zoom level.
   * @return {Number}
   * @access protected
   */
  _calculateMinZoom() {
    const { width, naturalWidth } = this._cropper.getImageData();
    return width / naturalWidth;
  }

  /**
   * It calculates if the image is rotated or not.
   * @return {Boolean}
   * @access protected
   */
  _isRotated() {
    const imageData = this._cropper.getImageData();
    return [90, 270].includes(Math.abs(imageData.rotate));
  }

  /**
   * It positions the canvas in the center of the container.
   * @param {Boolean} [centerLeft=true] Whether to center left dimension.
   * @param {Boolean} [centerTop=true]  Whether to center top dimension.
   * @access protected
   */
  _centerCanvas(centerLeft = true, centerTop = true) {
    const { width: containerWidth, height: containerHeight } = this._cropper.getContainerData();
    const { width: canvasWidth, height: canvasHeight } = this._cropper.getCanvasData();

    const positionObject = {};
    const halfFactor = 2;

    if (centerLeft) {
      positionObject.left = (containerWidth / halfFactor) - (canvasWidth / halfFactor);
    }

    if (centerTop) {
      positionObject.top = (containerHeight / halfFactor) - (canvasHeight / halfFactor);
    }

    this._cropper.setCanvasData(positionObject);
  }

  /**
   * Checks if the crop box is too small, if that is the case then it increase it size.
   * @access protected
   */
  _resizeCropBox() {
    const { width: cropBoxWidth, height: cropBoxHeight } = this._cropper.getCropBoxData();
    const { width: containerWidth, height: containerHeight } = this._cropper.getContainerData();

    if (cropBoxWidth < this._cropBoxSizeThreshold || cropBoxHeight < this._cropBoxSizeThreshold) {
      const updatedCropBoxWidth = cropBoxWidth * this._cropBoxSizeIncreaseFactor;
      const updatedCropBoxHeight = cropBoxHeight * this._cropBoxSizeIncreaseFactor;

      const halfFactor = 2;
      if (updatedCropBoxHeight < containerHeight && updatedCropBoxWidth < containerWidth) {
        this._cropper.setCropBoxData({
          width: updatedCropBoxWidth,
          height: updatedCropBoxHeight,
          left: (containerWidth / halfFactor) - (updatedCropBoxWidth / halfFactor),
          top: (containerHeight / halfFactor) - (updatedCropBoxHeight / halfFactor),
        });
      }
    }
  }

  /**
   * When the canvas starts being moved, it will check if the image is fitted to the crop box and
   * which dimension is fitted or if is a perfect fit.
   * @access protected
   */
  _moveStart() {
    const canvasData = this._cropper.getCanvasData();
    const cropBoxData = this._cropper.getCropBoxData();

    const isWidthFit = this.fit &&
      Math.round(cropBoxData.width) === Math.round(canvasData.width);

    const isHeightFit = this.fit &&
      Math.round(cropBoxData.height) === Math.round(canvasData.height);

    this._isFitted = isWidthFit || isHeightFit;
  }

  /**
   * Checks if the canvas is greater in width than the crop box. If this the case then limits the canvas movement
   * so it won't go beyond the canvas size; if the canvas is smaller, then limit canvas movement to inside the crop box.
   * These checks are being made for width and left dimensions.
   * @param {Boolean} isBiggerThanCropbox Whether the canvas is bigger in width than the crop box.
   * @return {Boolean} Returns if the crop box was moved, resulting in false if it tried to go past the limits.
   * @access protected
   */
  _limitCanvasLeftMovement(isBiggerThanCropbox) {
    const canvasData = this._cropper.getCanvasData();
    const cropBoxData = this._cropper.getCropBoxData();
    let hasMoved = true;

    if (isBiggerThanCropbox) {
      if (canvasData.left > cropBoxData.left) {
        this._cropper.setCanvasData({ left: cropBoxData.left });
        hasMoved = false;
      }
      if (canvasData.left + canvasData.width <= cropBoxData.left + cropBoxData.width) {
        this._cropper.setCanvasData({ left: cropBoxData.left + cropBoxData.width - canvasData.width });
        hasMoved = false;
      }
    } else {
      if (canvasData.left < cropBoxData.left) {
        this._cropper.setCanvasData({ left: cropBoxData.left });
        hasMoved = false;
      }
      if (canvasData.left > cropBoxData.left + cropBoxData.width - canvasData.width) {
        this._cropper.setCanvasData({ left: cropBoxData.left + cropBoxData.width - canvasData.width });
        hasMoved = false;
      }
    }

    return hasMoved;
  }

  /**
   * Checks if the canvas is greater in height than the crop box. If this the case then limits the canvas movement
   * to no to go beyond the canvas size, if the canvas is small then limit canvas movement to inside the crop box.
   * This checks are being made for height and top dimensions.
   * @param {Boolean} isBiggerThanCropbox Whether the canvas is bigger in height than the crop box.
   * @return {Boolean} Returns if the crop box was moved, resulting in false if it tried to go past the limits.
   * @access protected
   */
  _limitCanvasTopMovement(isBiggerThanCropbox) {
    const canvasData = this._cropper.getCanvasData();
    const cropBoxData = this._cropper.getCropBoxData();
    let hasMoved = true;

    if (isBiggerThanCropbox) {
      if (canvasData.top > cropBoxData.top) {
        this._cropper.setCanvasData({ top: cropBoxData.top });
        hasMoved = false;
      }

      if (canvasData.top + canvasData.height <= cropBoxData.top + cropBoxData.height) {
        this._cropper.setCanvasData({ top: cropBoxData.top + cropBoxData.height - canvasData.height });
        hasMoved = false;
      }
    } else {
      if (canvasData.top < cropBoxData.top) {
        this._cropper.setCanvasData({ top: cropBoxData.top });
        hasMoved = false;
      }
      if (canvasData.top > cropBoxData.top + cropBoxData.height - canvasData.height) {
        this._cropper.setCanvasData({ top: cropBoxData.top + cropBoxData.height - canvasData.height });
        hasMoved = false;
      }
    }

    return hasMoved;
  }

  /**
   * When the cropper canvas is being moved, checks if the canvas is trying to go beyond the boundaries
   * of the crop box relative to the canvas size. It will also just trigger the `onMove` callback only if
   * the canvas was moved and without going beyond the boundaries.
   * If the crop box is resized below the limit passed in `minCropBoxWidth` binding, it
   * sets the crop box width to that limit again. This can be used to avi=oid losing quality in the crop.
   * @access protected
   */
  _move() {
    let hasMoved = true;

    if (this.fixedCropBox) {
      const canvasData = this._cropper.getCanvasData();
      const cropBoxData = this._cropper.getCropBoxData();

      if (!this._isFitted) {
        const biggerWidthThanCropBox = canvasData.width > cropBoxData.width;
        const biggerHeightThanCropBox = canvasData.height > cropBoxData.height;

        hasMoved = this._limitCanvasLeftMovement(biggerWidthThanCropBox);
        hasMoved = this._limitCanvasTopMovement(biggerHeightThanCropBox);
      } else {
        if (this._fitDimension === 'height') {
          this._centerCanvas(false, true);
          hasMoved = this._limitCanvasLeftMovement(false);
        } else if (this._fitDimension === 'width') {
          this._centerCanvas(true, false);
          hasMoved = this._limitCanvasTopMovement(false);
        } else {
          this._centerCanvas();
        }
        hasMoved = false;
      }
    } else {
      const cropBoxData = this._cropper.getCropBoxData();
      if (this.minCropBoxWidth > cropBoxData.width) {
        this._cropper.setCropBoxData({ width: this.minCropBoxWidth });
      }
    }

    if (hasMoved) {
      this.onMove();
    }
  }

  /**
   * It calculates and sets the aspect ratio based on the image size.
   * @access protected
   */
  _autoAspectRatio() {
    const { naturalWidth, naturalHeight } = this._cropper.getImageData();
    if (this._isRotated()) {
      this._cropper.setAspectRatio(naturalHeight / naturalWidth);
    } else {
      this._cropper.setAspectRatio(naturalWidth / naturalHeight);
    }
  }

  /**
   * It handles behavior when the cropper is ready, checking with behavior to implement based on the
   * `fixedCropBox` value and executes the `onReady` callback.
   * @access protected
   */
  _handleCropperReady() {
    this.isOrientationRotated = this._isRotated();

    if (this.fixedCropBox) {
      this._resizeCropBox();
    }

    if (!this.aspectRatio) {
      this._autoAspectRatio();
    }

    if (this.fill) {
      this._fill(false);
    } else {
      this._fit(false);
    }

    this.onReady({ cropper: this._cropper, minZoomLevel: this._calculateMinZoom() });
  }

  /**
   * Once the image is loaded it mounts the cropper on the image element.
   * @access protected
   */
  _mountCropper() {
    this._cropper = new Cropper(this._imageCanvas, {
      viewMode: this.fixedCropBox ? 0 : 1,
      dragMode: this.fixedCropBox ? 'move' : 'drag',
      cropBoxMovable: !this.fixedCropBox,
      cropBoxResizable: !this.fixedCropBox,
      background: false,
      zoomOnTouch: false,
      zoomOnWheel: false,
      aspectRatio: this.aspectRatio,
      toggleDragModeOnDblclick: false,
      checkCrossOrigin: this.checkCrossOrigin,
      checkOrientation: this.checkOrientation,
      ready: this._handleCropperReady.bind(this),
      zoom: () => {
        this.onZoom();
      },
      cropstart: !this.fixedCropBox || this._moveStart.bind(this),
      cropmove: this._move.bind(this),
    });
  }
}

/**
 * @ngdoc component
 * @name odsImageCropper
 * @description
 * This component is a wrapper for the cropper js library.
 */
export default {
  /**
   * The controller class for the component.
   * @type {ImageCropperController}
   */
  controller: ImageCropperController,
  /**
   * The HTML template for the component.
   * @type {String}
   */
  template,
  /**
   * The component bindings/attributes
   * @type {Object}
   * @property {String}   image                     URL of the image on which the cropper component will be mounted.
   * @property {Boolean}  checkCrossOrigin          Flag used to cropper check if the current image is a cross-origin image.
   * @property {Boolean}  checkOrientation          Flag used to cropper check the current image's Exif Orientation information.
   * @property {String}   cropBoxSizeThreshold      Minimum size for the crop box, lower than this and the crop box
   *                                                sized will be increased.
   * @property {String}   cropBoxSizeIncreaseFactor Factor to increase the crop box size by.
   * @property {Number}   aspectRatio               Aspect ratio for the crop box.
   * @property {Number}   minCropBoxWidth           The minimum width of the crop box.
   * @property {Number}   zoomFactor                Number of steps for the zoom.
   * @property {Number}   rotateFactor              Degrees to rotate the image.
   * @property {Boolean}  reset                     Flag used to evaluate if the cropper needs to be reset or not.
   * @property {Boolean}  crop                      Flag used to evaluate if the cropper needs to execute the crop
   *                                                action.
   * @property {Boolean}  fit                       Flag used to evaluate if the image needs to fit the crop box.
   * @property {Boolean}  fill                      Flag used to evaluate if the image needs to fill the crop box,
   *                                                fitting the container.
   * @property {Boolean}  fixedCropBox              Tells the component whether to implement behavior for fixed
   *                                                crop box or not.
   * @property {Function} onMove                    Callback triggered when the cropper image is moved.
   * @property {Function} onZoom                    Callback triggered when the cropper image is zoomed in/out.
   * @property {Function} onRotate                  Callback triggered when the cropper image is rotated.
   * @property {Function} onReset                   Callback triggered when the cropper image is reset.
   * @property {Function} onReady                   Callback triggered once the cropper is mounted on the image.
   * @property {Function} onCrop                    Callback triggered once the crop action is executed.
   * @property {Function} onFit                     Callback triggered once the fit action is executed.
   * @property {Function} onFill                    Callback triggered once the fill action is executed.
   */
  bindings: {
    image: '@',
    checkCrossOrigin: '<',
    checkOrientation: '<',
    cropBoxSizeThreshold: '<',
    cropBoxSizeIncreaseFactor: '<',
    aspectRatio: '<',
    minCropBoxWidth: '<',
    zoomFactor: '<',
    rotateFactor: '<',
    reset: '<',
    crop: '<',
    fit: '<',
    fill: '<',
    fixedCropBox: '<',
    onMove: '&',
    onZoom: '&',
    onRotate: '&',
    onReset: '&',
    onReady: '&',
    onCrop: '&',
    onFit: '&',
    onFill: '&',
  },
};
